import { defineTokens } from '@chakra-ui/react'

/**
 * Primitive color tokens — confirmed hex values from hivewatch-colors.csv
 * Source: HiveWatch Figma design system palette export
 */
export const primitiveColors = defineTokens.colors({
  transparent: { value: 'transparent' },
  current: { value: 'currentColor' },
  black: { value: '#000000' },
  white: { value: '#FFFFFF' },
  // Legacy
  accent: { blue: { value: '#3D7FCE' } },
  legacy: {
    appBg: { value: '#F0F1F5' },
    brandBlue: { value: '#2E6CDB' },
    teal: { value: '#14B8A6' },
    toggleTrack: { value: '#D1D5DB' },
  },

  // ─── Dark Blue ───────────────────────────────────────────────────────────────
  // Primary dark background ramp (app/sidebar/card backgrounds)
  darkBlue: {
    50: { value: '#e6eaed' },
    100: { value: '#b3bfc6' },
    200: { value: '#80949f' },
    300: { value: '#4d6978' },
    400: { value: '#264b5b' },
    500: { value: '#002233' },
    600: { value: '#001e2d' },
    700: { value: '#001824' },
    800: { value: '#00121b' },
    900: { value: '#000a0f' },
  },

  // ─── Dusk Blue ───────────────────────────────────────────────────────────────
  // Secondary deep-blue surface ramp (inputs, tertiary borders)
  duskBlue: {
    50: { value: '#e8eef2' },
    100: { value: '#bcd0da' },
    200: { value: '#90b1c2' },
    300: { value: '#6492aa' },
    400: { value: '#447c98' },
    500: { value: '#11425d' },
    600: { value: '#0f3b52' },
    700: { value: '#0c3145' },
    800: { value: '#092738' },
    900: { value: '#051a25' },
  },

  // ─── Dawn Blue ───────────────────────────────────────────────────────────────
  // Bright accent blue ramp (focus rings, info states, glows)
  dawnBlue: {
    50: { value: '#f7fafd' },
    100: { value: '#e8f0f8' },
    200: { value: '#c0d6ea' },
    300: { value: '#a8c7e3' },
    400: { value: '#8fb8dc' },
    500: { value: '#77a9d5' },
    600: { value: '#5f9ace' },
    700: { value: '#4a84b8' },
    800: { value: '#3a6a94' },
    900: { value: '#2a4f70' },
  },

  // ─── Ghost Protocol ──────────────────────────────────────────────────────────
  // Muted blue-gray ramp (text, borders, disabled states)
  ghostProtocol: {
    50: { value: '#f8f7f9' },
    100: { value: '#eceaee' },
    200: { value: '#c5c0c9' },
    300: { value: '#b3adb8' },
    400: { value: '#a199a7' },
    500: { value: '#8f8596' },
    600: { value: '#7d7185' },
    700: { value: '#6b5d74' },
    800: { value: '#564a5d' },
    900: { value: '#413746' },
    950: { value: '#272429' },
  },

  // ─── Signal Yellow ───────────────────────────────────────────────────────────
  // Brand accent — replaces old teal (#14B8A6) as primary interactive color
  signalYellow: {
    50: { value: '#fcfff4' },
    100: { value: '#f6ffd9' },
    200: { value: '#eeffad' },
    300: { value: '#e6ff81' },
    400: { value: '#ddfe55' },
    500: { value: '#d4ff29' },
    600: { value: '#bfe600' },
    700: { value: '#9dbd00' },
    800: { value: '#7a9400' },
    900: { value: '#576b00' },
  },

  // ─── Overwatch ───────────────────────────────────────────────────────────────
  // Gold/warm accent ramp
  overwatch: {
    50: { value: '#fefdfb' },
    100: { value: '#f6f2e8' },
    200: { value: '#ede6d1' },
    300: { value: '#e4daba' },
    400: { value: '#dbcea3' },
    500: { value: '#d2c28c' },
    600: { value: '#c9b675' },
    700: { value: '#b39e5e' },
    800: { value: '#8f7e4b' },
    900: { value: '#6b5e38' },
    950: { value: '#373224' },
  },

  // ─── Green ───────────────────────────────────────────────────────────────────
  green: {
    50: { value: '#e6fbf1' },
    100: { value: '#b3f4d6' },
    200: { value: '#80edbb' },
    300: { value: '#4de6a0' },
    400: { value: '#26df80' },
    500: { value: '#00c054' },
    600: { value: '#00a047' },
    700: { value: '#00803a' },
    800: { value: '#005f2b' },
    900: { value: '#003d1c' },
  },

  // ─── Red ─────────────────────────────────────────────────────────────────────
  red: {
    50: { value: '#fdeaec' },
    100: { value: '#f9bfc4' },
    200: { value: '#f5949c' },
    300: { value: '#f16974' },
    400: { value: '#ed3e4c' },
    500: { value: '#d0303c' },
    600: { value: '#a62630' },
    700: { value: '#7c1c24' },
    800: { value: '#521318' },
    900: { value: '#3d0d0f' },
  },

  // ─── Orange ──────────────────────────────────────────────────────────────────
  orange: {
    50: { value: '#fff3eb' },
    100: { value: '#ffd9c1' },
    200: { value: '#ffbf97' },
    300: { value: '#ffa56d' },
    400: { value: '#ff8c42' },
    500: { value: '#ed6100' },
    600: { value: '#c35000' },
    700: { value: '#993e00' },
    800: { value: '#6f2d00' },
    900: { value: '#4d1f00' },
  },

  // ─── Yellow ──────────────────────────────────────────────────────────────────
  yellow: {
    50: { value: '#fffbeb' },
    100: { value: '#fff3c1' },
    200: { value: '#feeb97' },
    300: { value: '#fee36d' },
    400: { value: '#fddc51' },
    500: { value: '#e8cc00' },
    600: { value: '#bf9d00' },
    700: { value: '#967a00' },
    800: { value: '#6d5700' },
    900: { value: '#4d3d00' },
  },
  teal: { value: '#12B8B0' },
  'teal-blue': { value: '#12B779' },
  'teal-green': { value: '#1FB812' },
  // ─── Beacon Green ─────────────────────────────────────────────────────────────
  beaconGreen: {
    50: { value: '#e6faf6' },
    100: { value: '#c7f2dd' },
    200: { value: '#8fe3bc' },
    300: { value: '#4fd49b' },
    400: { value: '#12b8b0' },
    500: { value: '#12b779' },
    600: { value: '#1fb812' },
    700: { value: '#178a0e' },
    800: { value: '#0f5c09' },
    900: { value: '#082e05' },
    950: { value: '#002927' },
    '950-30': { value: '#0029274d' },
    'deep-lime': { value: '#052c02' },
    'deep-mint': { value: '#1d2c26' },
    'deep-teal': { value: '#181d27' },
  },
})
